import { omit } from 'lodash';

import { CustomerMasterSourceType, DocsApprovingEntity, DocumentSearchScopeType } from 'generated/bffDocument/models';
import { apiClient, ExtendedAxiosResponse } from 'helpers/apiClient';
import dayjs from 'helpers/dayjs';

import { ChannelType } from 'models/ChannelType';
import { DocumentsFilterParams } from 'models/Document';
import { Pagination } from 'models/Pagination';

export const api = {
  getDocumentList: (
    url: string,
    filter: DocumentsFilterParams,
    pagination: Pagination
  ): Promise<ExtendedAxiosResponse> => {
    filter.dateFrom = filter.dateFrom ? dayjs(filter.dateFrom).local().startOf('day').format() : undefined;
    filter.dateTo = filter.dateTo ? dayjs(filter.dateTo).local().endOf('day').format() : undefined;

    return apiClient.get(`${url}/document/documents`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        searchScope: DocumentSearchScopeType.LISTING_UNIVERSAL,
        ...filter,
        ...omit(pagination, ['initPages']),
        forceCheck: false,
      },
    });
  },

  getDocumentDetail: (
    url: string,
    documentGUID: string,
    objectStoreName: string,
    forceCheck = false
  ): Promise<ExtendedAxiosResponse<Document>> =>
    apiClient.get(`${url}/document/documents`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        searchScope: DocumentSearchScopeType.DETAIL,
        documentGUID,
        objectStoreName,
        forceCheck,
      },
    }),

  deleteIndex: (
    url: string,
    documentGUID: string,
    objectStoreName: string,
    propertyValue: string,
    propertyName: string
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.delete(`${url}/document/property`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        propertyName,
        propertyValue,
        documentGUID,
        objectStoreName,
      },
    }),

  updateIndex: (
    url: string,
    documentGUID: string,
    objectStoreName: string,
    propertyValue: string,
    propertyName: string
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.put(
      `${url}/document/property`,
      {},
      {
        params: {
          channel: ChannelType.MO2_CARE,
          customerMasterSource: CustomerMasterSourceType.O2POP,
          propertyName,
          propertyValue,
          documentGUID,
          objectStoreName,
        },
      }
    ),

  downloadDocument: (url: string, documentGUID: string, objectStoreName: string): Promise<ExtendedAxiosResponse> =>
    apiClient.get(`${url}/document/documents`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        searchScope: DocumentSearchScopeType.DETAIL_WITH_URL,
        documentGUID,
        objectStoreName,
        forceCheck: true,
      },
    }),

  getExternalUsers: (
    url: string,
    cuRefNo: string,
    searchString: string,
    guiIScreenId: string,
    pageSize: number,
    page: number
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.get(`${url}/external-users/quick-search`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        cuRefNo,
        requestType: 'specific',
        guiIScreenId,
        searchString,
        page,
        pageSize,
      },
    }),

  getDocumentListForConfirmation: (url: string, cuRefNo: string, cpRefNo?: string): Promise<ExtendedAxiosResponse> =>
    apiClient.get(`${url}/documents-for-confirmation`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        forceCheck: false,
        cuRefNo,
        cpRefNo,
      },
    }),

  putDocumentListForConfirmation: (
    url: string,
    docsApprovingEntityList: Array<DocsApprovingEntity>,
    cuRefNo: string,
    cpRefNo?: string
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.put(
      `${url}/documents-for-confirmation`,
      {
        cuRefNo,
        cpRefNo,
        docsApprovingEntityList,
      },
      {
        params: {
          channel: ChannelType.MO2_CARE,
          customerMasterSource: CustomerMasterSourceType.O2POP,
        },
      }
    ),

  getDocumentForConfirmationDownloadInfo: (
    url: string,
    jobId: string,
    cuRefNo: string,
    cpRefNo?: string
  ): Promise<ExtendedAxiosResponse> =>
    apiClient.get(`${url}/documents-for-confirmation/download-info/${jobId}`, {
      params: {
        cuRefNo,
        cpRefNo,
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
      },
    }),

  // TODO same as call for getDocumentList except searchScope and date filter (+ necessity clean filter after call)
  getFinancialDocumentForSending: (url: string, filter: DocumentsFilterParams): Promise<ExtendedAxiosResponse> => {
    return apiClient.get(`${url}/document/documents`, {
      params: {
        channel: ChannelType.MO2_CARE,
        customerMasterSource: CustomerMasterSourceType.O2POP,
        searchScope: DocumentSearchScopeType.LISTING,
        caRefNo: filter.caRefNo,
        cuRefNo: filter.cuRefNo,
        documentId: filter.documentId,
      },
    });
  },
};
